import React from 'react';
import { Link } from 'react-router-dom';
export function RefundPoliciesContent() {
  return (
    <>
      <p className="intro border-l-4 border-blue-600 pl-5 mb-6 text-slate-600 text-base leading-relaxed">
        At Candor, we strive to provide reliable, secure, and high-quality AI and data platform services.
        This Refund Policy explains how subscription charges, cancellations, billing disputes, and
        refund requests are handled.
        All payments are securely processed through <strong>Paddle.com Market Ltd.</strong>, which acts
        as the <strong>Merchant of Record</strong> for all transactions.
      </p>

      <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">1. Subscription Services</h2>
          <p>
            Candor operates on a subscription-based billing model. Pricing for our services is displayed on our Pricing page and may include monthly, annual, usage-based, or custom plans.
          </p>
          <p className="mb-2">Plans may include:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Monthly plans</li>
            <li>Annual plans</li>
            <li>Usage-based services</li>
            <li>API/token-based consumption</li>
            <li>Add-on token packages</li>
          </ul>
          <p>
            By purchasing a subscription, you agree to recurring billing according to your selected plan. Subscription charges, billing frequency, and applicable fees will be displayed before checkout. Subscriptions renew automatically unless canceled before the next billing cycle. You may cancel your subscription at any time through your account settings, by contacting Paddle Support, or by contacting us at <a href="mailto:sales@candorsys.com" className="text-blue-600 hover:underline">sales@candorsys.com</a>.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">2. Digital Services</h2>
          <p>
            Candor provides digital software and AI-powered services. Access to digital services may begin immediately after purchase or account activation.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">3. Free Trial &amp; Evaluation</h2>
          <p>
            Where applicable, users are encouraged to evaluate the platform during any available free trial or testing period before purchasing a paid subscription.
          </p>
          <p className="mb-2">In accordance with Paddle’s global reseller standards:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li><strong>Standard Policy:</strong> Subscription charges incurred after a free trial period ends are generally non-refundable.</li>
            <li><strong>Statutory Post-Trial Right of Withdrawal:</strong> For eligible consumers residing in the European Union, European Economic Area (EEA), United Kingdom, and Switzerland, a statutory 14-calendar-day right of withdrawal applies immediately after the free trial period ends and the first paid billing event occurs. Refund requests made within this 14-day window will be fully honored, provided paid platform components or digital assets have not been downloaded or consumed in violation of our terms.</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">4. Refund Eligibility</h2>
          <p>
            In strict alignment with Paddle’s global reseller standards and regional consumer protection frameworks, we provide an unconditional refund window for our platform services:
          </p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li><strong>14-Day Money-Back Guarantee &amp; Right of Withdrawal:</strong> Customers are entitled to request a full refund for any initial purchase, first-time subscription cycle, or license fee within 14 calendar days from the transaction date. This 14-day window serves as our baseline refund eligibility period globally and is completely honored without requiring proof of technical fault.</li>
            <li><strong>Exceptional Adjustments:</strong> Refund requests submitted outside of this 14-day window are considered non-refundable, unless there is a verified, platform-side billing error, a double charge, or an extended service outage that directly prevents platform accessibility. All approved exceptions will be evaluated on a case-by-case basis and processed securely through our Merchant of Record, Paddle.</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">5. Non-Refundable Situations</h2>
          <p>Refunds are generally not provided for:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Partial subscription periods</li>
            <li>Unused API credits or token allocations</li>
            <li>Consumed API usage, generated outputs, compute usage, or partially consumed add-on credits</li>
            <li>Failure to cancel before subscription renewal</li>
            <li>User dissatisfaction unrelated to a verified technical issue</li>
            <li>Misconfiguration or third-party integration issues</li>
            <li>Violations of our <Link to="/terms-and-policies" className="text-blue-600 hover:underline">Terms &amp; Conditions</Link></li>
            <li>Abuse, fraud, or suspicious activity</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">6. Subscription Cancellation</h2>
          <p>
            Users may cancel their subscription at any time through their account settings or billing portal.
          </p>
          <p>
            After cancellation, the subscription remains active until the end of the current billing period, and no additional renewal charges will occur.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">7. Fraud Prevention &amp; Security</h2>
          <p>To protect our payment infrastructure and users, Candor actively monitors for:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Card testing attacks</li>
            <li>Automated payment abuse</li>
            <li>Fraudulent transactions</li>
            <li>Unauthorized billing activities</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">8. Chargebacks</h2>
          <p>
            Before initiating a chargeback, customers are encouraged to contact our support team directly at <a href="mailto:sales@candorsys.com" className="text-blue-600 hover:underline">sales@candorsys.com</a> for resolution assistance.
          </p>
          <p>Initiating fraudulent, abusive, or repeated chargebacks may result in:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Account suspension</li>
            <li>Permanent account termination</li>
            <li>Restriction of future purchases or services</li>
          </ul>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">9. Service Availability</h2>
          <p>
            Temporary interruptions caused by scheduled maintenance, third-party provider outages, internet/network failures, or force majeure events may not qualify for refunds.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">10. Taxes</h2>
          <p>
            Applicable taxes may be added based on your billing region and local regulatory requirements. Tax (VAT, GST, sales tax) is calculated and collected by Paddle where applicable.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">11. Refund Processing</h2>
          <p>
            Approved refunds are processed back to the original payment method within 5–10 business days, depending on Paddle and your banking institution.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">12. Enterprise Agreements</h2>
          <p>
            Enterprise agreements or custom customer contracts may be governed by separate billing, cancellation, and refund terms.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">13. Support Contact</h2>
          <p>For billing, refund, or subscription-related requests, please contact:</p>
          <p className="mt-2 font-medium">
            Proden Technologies Incorporated<br />
            30 N Gould St<br />
            Sheridan, Wyoming 82801, USA<br />
            Email: <a href="mailto:sales@candorsys.com" className="text-blue-600 hover:underline">sales@candorsys.com</a><br />
            Phone: <a href="tel:+17202355757" className="text-blue-600 hover:underline">+1 (720) 235-5757</a>
          </p>
          <p className="text-xs text-slate-500 mt-1">We aim to respond to billing inquiries within 3–5 business days.</p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">14. Policy Updates</h2>
          <p>
            Candor reserves the right to modify this Refund Policy at any time. Updated versions will be posted on our official website. Material changes may be communicated via email or in-app notification. Continued use of the Service after such updates constitutes acceptance of the revised Terms.
          </p>

          <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-100 pb-2">15. Consumer Rights</h2>
          <p>
            Nothing in this policy limits any rights provided under applicable consumer protection laws.
          </p>
        </div>
    </>
  );
}

export default function RefundPoliciesPage() {
  return (
    <div className="bg-slate-50 min-h-screen py-16 animate-fade-in font-sans">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 bg-white border border-slate-200 rounded-3xl p-8 sm:p-12 shadow-sm text-slate-700 leading-relaxed text-sm">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-2">
          Candor – Refund Policy
        </h1>
        <p className="text-slate-500 text-sm mb-6 border-b border-slate-100 pb-4">
          Effective Date: May 26, 2026 • All prices in USD
        </p>
        <RefundPoliciesContent />
      </div>
    </div>
  );
}

